import React, { useLayoutEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import type { AddressType } from '@sg/garnish';
import {
  BodyText,
  DisplayText,
  ProgressBar,
  switchcase,
  theme,
  useResponsive,
  useSafeExpoNavigation,
  useSafeNavigation,
} from '@sg/garnish';
import { FlattenedOrderStatuses } from '@sg/graphql-schema';

import {
  getDayDateTimeYear,
  getOrderTimeRange,
  ignoreTimezone,
} from '@order/utils';

import { getOrderTime } from '../../../../context/OrderStatus';
import { useDayLabel } from '../../hooks';
import { OrderStatusCtas } from '../OrderStatusCtas';

// ────────────────────────────────────────────────────────────────────────────────

export const OrderStatusInfo = (props: OrderStatusInfoProps) => {
  const {
    orderId = '',
    orderStatus,
    orderType,
    orderTotal = 0,
    vendorId,
    isPendingFeedback,
    momoSurveyUrl,
    canReorder,
    onReorder,
  } = props;

  const { minWidth, match } = useResponsive();

  // ────────────────────────────────────────────────────────────────

  const shouldShowProgressBar = checkIfShouldShowProgressBar(orderStatus);
  const shouldRenderOrderStatusCTAs = canReorder || isPendingFeedback;

  const orderStatusTitle = useOrderStatusTitle(props);
  const orderStatusDetails = useOrderStatusDetails(props);

  useOrderStatusScreenTitle(orderStatusTitle);

  // ─── Styles ──────────────────────────────────────────────────────

  const ctasWrapperStyle = match([undefined, styles.ctasWrapperSM]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <View testID="order-status-info">
      <OrderStatusInfoHeader title={orderStatusTitle} vendorId={vendorId} />

      {orderStatusDetails ? (
        <OrderStatusDetails details={orderStatusDetails} />
      ) : null}

      {shouldShowProgressBar ? (
        <OrderProgressBar orderType={orderType} orderStatus={orderStatus} />
      ) : null}

      {minWidth.isSM && shouldRenderOrderStatusCTAs ? (
        <View style={ctasWrapperStyle}>
          <OrderStatusCtas
            orderId={orderId}
            orderTotal={orderTotal}
            isPendingFeedback={isPendingFeedback}
            momoSurveyUrl={momoSurveyUrl}
            canReorder={canReorder}
            onReorder={onReorder}
          />
        </View>
      ) : null}
    </View>
  );
};

// ─── Subcomponents ───────────────────────────────────────────────────────────

const OrderStatusInfoHeader = (props: OrderStatusInfoHeaderProps) => {
  const { title, vendorId, ...restProps } = props;

  const { match } = useResponsive();

  const headerStyles = match([styles.infoHeaderXS, styles.infoHeaderSM]);

  return (
    <View style={headerStyles} {...restProps}>
      <OrderStatusTitle title={title} />
      <OrderStatusNumber vendorId={vendorId} />
    </View>
  );
};

const OrderStatusNumber = (props: OrderStatusNumberProps) => {
  const { vendorId: orderId } = props;

  const { currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();

  return (
    <BodyText size={4} bold={currentBreakpoint.isXS}>
      {formatMessage(messages.orderStatusInfoOrderNumber, {
        order_id: orderId,
      })}
    </BodyText>
  );
};

const OrderStatusTitle = (props: OrderStatusTitleProps) => {
  return <DisplayText sizeMatch={['32', '40']}>{props.title}</DisplayText>;
};

const OrderStatusDetails = (props: OrderStatusDetailsProps) => {
  const { details } = props;

  return <BodyText>{details}</BodyText>;
};

const OrderProgressBar = (props: OrderProgressBarProps) => {
  const { orderStatus, orderType } = props;

  const progressNodes = useProgressNodes(orderType, orderStatus);

  return (
    <View style={styles.progressBar}>
      <ProgressBar nodes={progressNodes} maxFontSizeMultiplier={1.3} />
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  orderStatusInfoOrderNumber: {
    defaultMessage: 'Order #{order_id}',
    description: 'Order Status Info | Order Number',
  },
  orderStatusInfoTitlePickupCompleted: {
    defaultMessage: 'Order completed',
    description: 'Order Status Info | Title | Pickup | Completed',
  },
  orderStatusInfoTitleFailed: {
    defaultMessage: 'Order canceled',
    description: 'Order Status Info | Title | Failed',
  },
  orderStatusInfoTitlePast: {
    defaultMessage: 'Order completed',
    description: 'Order Status Info | Title | Past',
  },
  orderStatusInfoTitlePickupUnknown: {
    defaultMessage: 'Pickup order',
    description: 'Order Status Info | Title | Pickup | Unknown',
  },
  orderStatusInfoTitleOutpostUnknown: {
    defaultMessage: 'Outpost order',
    description: 'Order Status Info | Title | Outpost | Unknown',
  },
  orderStatusInfoTitleDeliveryUnknown: {
    defaultMessage: 'Delivery order',
    description: 'Order Status Info | Title | Delivery | Unknown',
  },
  orderStatusUnavailable: {
    defaultMessage: 'Unavailable',
    description: 'Order Status | Unavailable',
  },
  orderStatusInfoProgressPickupReceived: {
    defaultMessage: 'Received',
    description: 'Order Status Info | Progress | Pickup | Received',
  },
  orderStatusInfoProgressPickupPreparing: {
    defaultMessage: 'Preparing',
    description: 'Order Status Info | Progress | Pickup | Preparing',
  },
  orderStatusInfoProgressPickupCompleted: {
    defaultMessage: 'Complete',
    description: 'Order Status Info | Progress | Pickup | Completed',
  },
  orderStatusInfoProgressOutpostReceived: {
    defaultMessage: 'Received',
    description: 'Order Status Info | Progress | Outpost | Received',
  },
  orderStatusInfoProgressOutpostDelivering: {
    defaultMessage: 'Delivering',
    description: 'Order Status Info | Progress | Outpost | Delivering',
  },
  orderStatusInfoProgressOutpostCompleted: {
    defaultMessage: 'Complete',
    description: 'Order Status Info | Progress | Outpost | Completed',
  },
  orderStatusInfoProgressDeliveryReceived: {
    defaultMessage: 'Received',
    description: 'Order Status Info | Progress | Delivery | Received',
  },
  orderStatusInfoProgressDeliveryPreparing: {
    defaultMessage: 'Preparing',
    description: 'Order Status Info | Progress | Delivery | Preparing',
  },
  orderStatusInfoProgressDeliveryCourier: {
    defaultMessage: 'Ready for courier',
    description: 'Order Status Info | Progress | Delivery | Courier',
  },
  orderStatusInfoProgressDeliveryDelivering: {
    defaultMessage: 'Delivering',
    description: 'Order Status Info | Progress | Delivery | Delivering',
  },
});

const orderStatusTitleMessages = defineMessages({
  orderStatusInfoTitleUnavailable: {
    defaultMessage: `{order_type, select,
      pickup {Pickup order}
      outpost {Outpost order}
      delivery {Delivery order}
      other {}
    }`,
    description: 'Order Status Info | Title | Unavailable',
  },
  orderStatusInfoTitlePickupReceived: {
    defaultMessage: 'Order received',
    description: 'Order Status Info | Title | Pickup | Received',
  },
  orderStatusInfoTitlePickupPreparing: {
    defaultMessage: 'Preparing order',
    description: 'Order Status Info | Title | Pickup | Preparing',
  },
  orderStatusInfoTitlePickupCompleted: {
    defaultMessage: 'Order completed',
    description: 'Order Status Info | Title | Pickup | Completed',
  },
  orderStatusInfoTitleOutpostReceived: {
    defaultMessage: 'Order received',
    description: 'Order Status Info | Title | Outpost | Received',
  },
  orderStatusInfoTitleOutpostDelivering: {
    defaultMessage: 'Order out for delivery',
    description: 'Order Status Info | Title | Outpost | Delivering',
  },
  orderStatusInfoTitleOutpostCompleted: {
    defaultMessage: 'Order completed',
    description: 'Order Status Info | Title | Outpost | Completed',
  },
  orderStatusInfoTitleDeliveryReceived: {
    defaultMessage: 'Order received',
    description: 'Order Status Info | Title | Delivery | Received',
  },
  orderStatusInfoTitleDeliveryPreparing: {
    defaultMessage: 'Preparing order',
    description: 'Order Status Info | Title | Delivery | Preparing',
  },
  orderStatusInfoTitleDeliveryCourier: {
    defaultMessage: 'Order ready for courier',
    description: 'Order Status Info | Title | Delivery | Courier',
  },
  orderStatusInfoTitleDeliveryDelivering: {
    defaultMessage: 'Order out for delivery',
    description: 'Order Status Info | Title | Delivery | Delivering',
  },
  orderStatusInfoTitleDeliveryCompleted: {
    defaultMessage: 'Order completed',
    description: 'Order Status Info | Title | Delivery | Completed',
  },
});

const orderStatusSubtitleMessages = defineMessages({
  orderStatusInfoSubtitleFailed: {
    defaultMessage:
      "We're so sorry we had to cancel your order, there was a snag with it. We are processing a full refund, which will post in your account within 3-5 business days.",
    description: 'Order Status Info | Subtitle | Failed',
  },
  orderStatusInfoSubtitlePast: {
    defaultMessage: 'Your order was completed {day}, {date} at {time}',
    description: 'Order Status Info | Subtitle | Past',
  },
  orderStatusInfoSubtitleUnavailable: {
    defaultMessage: `{order_type, select,
      pickup {Your order will be ready {day}, {date} at {time} at {name}, {address}. Unfortunately, we're unable to provide real-time order status tracking at this time.}
      outpost {Your order will be ready {day}, {date}, between {start_time}-{end_time} at the {name}. Unfortunately, we're unable to provide real-time order status tracking at this time.}
      delivery {Your order will be arriving {day}, {date}, {time}. Unfortunately, we're unable to provide real-time order status tracking at this time. Please contact us if your order does not show up around the scheduled delivery time.}
      other {}
    }`,
    description: 'Order Status Info | Subtitle | Unavailable',
  },
  orderStatusInfoSubtitlePickupReceived: {
    defaultMessage: 'Scheduled pickup time at {name} is {day} at {time}',
    description: 'Order Status Info | Subtitle | Pickup | Received',
  },
  orderStatusInfoSubtitlePickupPreparing: {
    defaultMessage: 'Scheduled pickup time at {name} is {day} at {time}',
    description: 'Order Status Info | Subtitle | Pickup | Preparing',
  },
  orderStatusInfoSubtitlePickupCompleted: {
    defaultMessage: 'Your order was completed {day} at {time}',
    description: 'Order Status Info | Subtitle | Pickup | Completed',
  },
  orderStatusInfoSubtitleOutpostReceived: {
    defaultMessage: 'Estimated delivery {day} between {start_time}-{end_time}',
    description: 'Order Status Info | Subtitle | Outpost | Received',
  },
  orderStatusInfoSubtitleOutpostDelivering: {
    defaultMessage: 'Estimated delivery {day} between {start_time}-{end_time}',
    description: 'Order Status Info | Subtitle | Outpost | Delivering',
  },
  orderStatusInfoSubtitleOutpostCompleted: {
    defaultMessage: 'Your order was completed {day} at {time}',
    description: 'Order Status Info | Subtitle | Outpost | Completed',
  },
  orderStatusInfoSubtitleDeliveryReceived: {
    defaultMessage: 'Scheduled delivery is {day} {start_time}-{end_time}',
    description: 'Order Status Info | Subtitle | Delivery | Received',
  },
  orderStatusInfoSubtitleDeliveryPreparing: {
    defaultMessage: 'Estimated delivery is {day} {start_time}-{end_time}',
    description: 'Order Status Info | Subtitle | Delivery | Preparing',
  },
  orderStatusInfoSubtitleDeliveryCourier: {
    defaultMessage: 'Estimated delivery is {day} {start_time}-{end_time}',
    description: 'Order Status Info | Subtitle | Delivery | Courier',
  },
  orderStatusInfoSubtitleDeliveryRoute: {
    defaultMessage: 'Estimated delivery is {day} {start_time}-{end_time}',
    description: 'Order Status Info | Subtitle | Delivery | Route',
  },
  orderStatusInfoSubtitleDeliveryCompleted: {
    defaultMessage: 'Your order was completed {day} at {time}',
    description: 'Order Status Info | Subtitle | Delivery | Completed',
  },
});

const orderStatusDateMessages = defineMessages({
  orderStatusInfoWeekdaySunday: {
    defaultMessage: 'Sunday',
    description: 'Order Status Info | Weekday | Sunday',
  },
  orderStatusInfoWeekdayMonday: {
    defaultMessage: 'Monday',
    description: 'Order Status Info | Weekday | Monday',
  },
  orderStatusInfoWeekdayTuesday: {
    defaultMessage: 'Tuesday',
    description: 'Order Status Info | Weekday | Tuesday',
  },
  orderStatusInfoWeekdayWednesday: {
    defaultMessage: 'Wednesday',
    description: 'Order Status Info | Weekday | Wednesday',
  },
  orderStatusInfoWeekdayThursday: {
    defaultMessage: 'Thursday',
    description: 'Order Status Info | Weekday | Thursday',
  },
  orderStatusInfoWeekdayFriday: {
    defaultMessage: 'Friday',
    description: 'Order Status Info | Weekday | Friday',
  },
  orderStatusInfoWeekdaySaturday: {
    defaultMessage: 'Saturday',
    description: 'Order Status Info | Weekday | Saturday',
  },
  orderStatusInfoMonthJanuary: {
    defaultMessage: 'January',
    description: 'Order Status Info | Month | January',
  },
  orderStatusInfoMonthFebruary: {
    defaultMessage: 'February',
    description: 'Order Status Info | Month | February',
  },
  orderStatusInfoMonthMarch: {
    defaultMessage: 'March',
    description: 'Order Status Info | Month | March',
  },
  orderStatusInfoMonthApril: {
    defaultMessage: 'April',
    description: 'Order Status Info | Month | April',
  },
  orderStatusInfoMonthMay: {
    defaultMessage: 'May',
    description: 'Order Status Info | Month | May',
  },
  orderStatusInfoMonthJune: {
    defaultMessage: 'June',
    description: 'Order Status Info | Month | June',
  },
  orderStatusInfoMonthJuly: {
    defaultMessage: 'July',
    description: 'Order Status Info | Month | July',
  },
  orderStatusInfoMonthAugust: {
    defaultMessage: 'August',
    description: 'Order Status Info | Month | August',
  },
  orderStatusInfoMonthSeptember: {
    defaultMessage: 'September',
    description: 'Order Status Info | Month | September',
  },
  orderStatusInfoMonthOctober: {
    defaultMessage: 'October',
    description: 'Order Status Info | Month | October',
  },
  orderStatusInfoMonthNovember: {
    defaultMessage: 'November',
    description: 'Order Status Info | Month | November',
  },
  orderStatusInfoMonthDecember: {
    defaultMessage: 'December',
    description: 'Order Status Info | Month | December',
  },
});

const orderStatusInfoDateMessageMap = {
  'general.weekday.sunday':
    orderStatusDateMessages.orderStatusInfoWeekdaySunday,
  'general.weekday.monday':
    orderStatusDateMessages.orderStatusInfoWeekdayMonday,
  'general.weekday.tuesday':
    orderStatusDateMessages.orderStatusInfoWeekdayTuesday,
  'general.weekday.wednesday':
    orderStatusDateMessages.orderStatusInfoWeekdayWednesday,
  'general.weekday.thursday':
    orderStatusDateMessages.orderStatusInfoWeekdayThursday,
  'general.weekday.friday':
    orderStatusDateMessages.orderStatusInfoWeekdayFriday,
  'general.weekday.saturday':
    orderStatusDateMessages.orderStatusInfoWeekdaySaturday,
  'general.month.january': orderStatusDateMessages.orderStatusInfoMonthJanuary,
  'general.month.february':
    orderStatusDateMessages.orderStatusInfoMonthFebruary,
  'general.month.march': orderStatusDateMessages.orderStatusInfoMonthMarch,
  'general.month.april': orderStatusDateMessages.orderStatusInfoMonthApril,
  'general.month.may': orderStatusDateMessages.orderStatusInfoMonthMay,
  'general.month.june': orderStatusDateMessages.orderStatusInfoMonthJune,
  'general.month.july': orderStatusDateMessages.orderStatusInfoMonthJuly,
  'general.month.august': orderStatusDateMessages.orderStatusInfoMonthAugust,
  'general.month.september':
    orderStatusDateMessages.orderStatusInfoMonthSeptember,
  'general.month.october': orderStatusDateMessages.orderStatusInfoMonthOctober,
  'general.month.november':
    orderStatusDateMessages.orderStatusInfoMonthNovember,
  'general.month.december':
    orderStatusDateMessages.orderStatusInfoMonthDecember,
} as const;

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  infoHeaderXS: {
    gap: theme.spacing['4'],
  },
  infoHeaderSM: {
    gap: theme.spacing['4'],
    marginBottom: theme.spacing['4'],
    flexDirection: 'column-reverse',
  },
  progressBar: {
    marginTop: theme.spacing['4'],
    paddingTop: theme.spacing['4'],
    minHeight: theme.spacing['10'],
  },
  ctasWrapperSM: {
    paddingTop: theme.spacing['4'],
  },
});

// ─── Hooks ──────────────────────────────────────────────────────────────────────

const useOrderStatusScreenTitle = (orderStatusText: string) => {
  const navigation = useSafeNavigation();
  const expoNavigation = useSafeExpoNavigation();
  const setOptions = navigation?.setOptions ?? expoNavigation?.setOptions;

  useLayoutEffect(() => {
    setOptions?.({ title: orderStatusText });
  }, [setOptions, orderStatusText]);
};

const useOrderStatusTitle = (props: UseOrderStatusTitleProps) => {
  const { orderType, orderStatus, isPast, isTrackable } = props;

  const { formatMessage } = useIntl();

  const isFailed = orderStatus === FlattenedOrderStatuses.Failed;
  const options = { order_type: orderType };

  // ─────────────────────────────────────────────────────────────────

  if (orderType === 'in-store') {
    return formatMessage(messages.orderStatusInfoTitlePickupCompleted);
  }

  if (isFailed) return formatMessage(messages.orderStatusInfoTitleFailed);

  if (isPast) return formatMessage(messages.orderStatusInfoTitlePast);

  if (!isTrackable) {
    return formatMessage(
      orderStatusTitleMessages.orderStatusInfoTitleUnavailable,
      options,
    );
  }

  if (orderType === 'pickup') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(
        orderStatusTitleMessages.orderStatusInfoTitlePickupReceived,
      ),
      [FlattenedOrderStatuses.Preparing]: formatMessage(
        orderStatusTitleMessages.orderStatusInfoTitlePickupPreparing,
      ),
      [FlattenedOrderStatuses.Completed]: formatMessage(
        orderStatusTitleMessages.orderStatusInfoTitlePickupCompleted,
      ),
    })(formatMessage(messages.orderStatusInfoTitlePickupUnknown))(
      orderStatus ?? FlattenedOrderStatuses.Failed,
    );
  }

  if (orderType === 'outpost') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(
        orderStatusTitleMessages.orderStatusInfoTitleOutpostReceived,
      ),
      [FlattenedOrderStatuses.Delivering]: formatMessage(
        orderStatusTitleMessages.orderStatusInfoTitleOutpostDelivering,
      ),
      [FlattenedOrderStatuses.Completed]: formatMessage(
        orderStatusTitleMessages.orderStatusInfoTitleOutpostCompleted,
      ),
    })(formatMessage(messages.orderStatusInfoTitleOutpostUnknown))(
      orderStatus ?? FlattenedOrderStatuses.Failed,
    );
  }

  if (orderType === 'delivery') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(
        orderStatusTitleMessages.orderStatusInfoTitleDeliveryReceived,
      ),
      [FlattenedOrderStatuses.Preparing]: formatMessage(
        orderStatusTitleMessages.orderStatusInfoTitleDeliveryPreparing,
      ),
      [FlattenedOrderStatuses.ReadyForCourier]: formatMessage(
        orderStatusTitleMessages.orderStatusInfoTitleDeliveryCourier,
      ),
      [FlattenedOrderStatuses.Delivering]: formatMessage(
        orderStatusTitleMessages.orderStatusInfoTitleDeliveryDelivering,
      ),
      [FlattenedOrderStatuses.Completed]: formatMessage(
        orderStatusTitleMessages.orderStatusInfoTitleDeliveryCompleted,
      ),
    })(formatMessage(messages.orderStatusInfoTitleDeliveryUnknown))(
      orderStatus ?? FlattenedOrderStatuses.Failed,
    );
  }

  return formatMessage(messages.orderStatusUnavailable);
};

const useOrderStatusDetails = (props: UseOrderStatusDetailsProps) => {
  const { formatMessage } = useIntl();
  const {
    orderStatus,
    orderType,
    isPast,
    isTrackable,
    wantedTime,
    estimatedDeliveryTime = '',
    locationName: name,
    locationAddress: address,
  } = props;

  const { startTime, endTime, timeEstimate } = useMemo(() => {
    const orderTime = getOrderTime({
      wantedTime,
      deliveryOrderDetail: { estimatedDeliveryTime },
    });
    const estimate = orderTime ? ignoreTimezone(orderTime) : undefined;

    return { ...getOrderTimeRange(estimate), timeEstimate: estimate };
  }, [wantedTime, estimatedDeliveryTime]);

  const isFailed = orderStatus === FlattenedOrderStatuses.Failed;

  const day = useDayLabel(timeEstimate);
  const formatDateMessage = (key: string) =>
    formatMessage(
      orderStatusInfoDateMessageMap[
        key as keyof typeof orderStatusInfoDateMessageMap
      ],
    );
  const {
    date = '',
    time = '',
    year = '',
  } = timeEstimate ? getDayDateTimeYear(formatDateMessage, timeEstimate) : {};

  const localizationValues = {
    name,
    address,
    day,
    date,
    time,
    start_time: startTime,
    end_time: endTime,
    year,
    order_type: orderType,
  };

  if (orderType === 'in-store') {
    return formatMessage(
      orderStatusSubtitleMessages.orderStatusInfoSubtitlePickupCompleted,
      localizationValues,
    );
  }

  if (isFailed) {
    return formatMessage(
      orderStatusSubtitleMessages.orderStatusInfoSubtitleFailed,
      localizationValues,
    );
  }

  if (isPast) {
    return formatMessage(
      orderStatusSubtitleMessages.orderStatusInfoSubtitlePast,
      localizationValues,
    );
  }

  if (!isTrackable) {
    return formatMessage(
      orderStatusSubtitleMessages.orderStatusInfoSubtitleUnavailable,
      localizationValues,
    );
  }

  if (orderType === 'pickup') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(
        orderStatusSubtitleMessages.orderStatusInfoSubtitlePickupReceived,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Preparing]: formatMessage(
        orderStatusSubtitleMessages.orderStatusInfoSubtitlePickupPreparing,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Completed]: formatMessage(
        orderStatusSubtitleMessages.orderStatusInfoSubtitlePickupCompleted,
        localizationValues,
      ),
    })(
      formatMessage(
        orderStatusSubtitleMessages.orderStatusInfoSubtitleUnavailable,
        localizationValues,
      ),
    )(orderStatus ?? FlattenedOrderStatuses.Received);
  }

  if (orderType === 'outpost') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(
        orderStatusSubtitleMessages.orderStatusInfoSubtitleOutpostReceived,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Delivering]: formatMessage(
        orderStatusSubtitleMessages.orderStatusInfoSubtitleOutpostDelivering,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Completed]: formatMessage(
        orderStatusSubtitleMessages.orderStatusInfoSubtitleOutpostCompleted,
        localizationValues,
      ),
    })(
      formatMessage(
        orderStatusSubtitleMessages.orderStatusInfoSubtitleUnavailable,
        localizationValues,
      ),
    )(orderStatus ?? FlattenedOrderStatuses.Received);
  }

  if (orderType === 'delivery') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(
        orderStatusSubtitleMessages.orderStatusInfoSubtitleDeliveryReceived,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Preparing]: formatMessage(
        orderStatusSubtitleMessages.orderStatusInfoSubtitleDeliveryPreparing,
        localizationValues,
      ),
      [FlattenedOrderStatuses.ReadyForCourier]: formatMessage(
        orderStatusSubtitleMessages.orderStatusInfoSubtitleDeliveryCourier,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Delivering]: formatMessage(
        orderStatusSubtitleMessages.orderStatusInfoSubtitleDeliveryRoute,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Completed]: formatMessage(
        orderStatusSubtitleMessages.orderStatusInfoSubtitleDeliveryCompleted,
        localizationValues,
      ),
    })(
      formatMessage(
        orderStatusSubtitleMessages.orderStatusInfoSubtitleUnavailable,
        localizationValues,
      ),
    )(orderStatus ?? FlattenedOrderStatuses.Received);
  }

  return formatMessage(
    orderStatusSubtitleMessages.orderStatusInfoSubtitleUnavailable,
    localizationValues,
  );
};

const useProgressNodes = (
  orderType?: AddressType,
  orderStatus?: FlattenedOrderStatuses,
) => {
  const { formatMessage } = useIntl();

  if (orderType === 'pickup') {
    return [
      {
        id: FlattenedOrderStatuses.Received,
        label: formatMessage(messages.orderStatusInfoProgressPickupReceived),
        completed: [
          FlattenedOrderStatuses.Received,
          FlattenedOrderStatuses.Preparing,
          FlattenedOrderStatuses.Completed,
        ].includes(orderStatus ?? FlattenedOrderStatuses.Failed),
      },
      {
        id: FlattenedOrderStatuses.Preparing,
        label: formatMessage(messages.orderStatusInfoProgressPickupPreparing),
        completed: [
          FlattenedOrderStatuses.Preparing,
          FlattenedOrderStatuses.Completed,
        ].includes(orderStatus ?? FlattenedOrderStatuses.Failed),
      },
      {
        id: FlattenedOrderStatuses.Completed,
        label: formatMessage(messages.orderStatusInfoProgressPickupCompleted),
        completed: [FlattenedOrderStatuses.Completed].includes(
          orderStatus ?? FlattenedOrderStatuses.Failed,
        ),
      },
    ];
  }

  if (orderType === 'outpost') {
    return [
      {
        id: FlattenedOrderStatuses.Received,
        label: formatMessage(messages.orderStatusInfoProgressOutpostReceived),
        completed: [
          FlattenedOrderStatuses.Received,
          FlattenedOrderStatuses.Delivering,
          FlattenedOrderStatuses.Completed,
        ].includes(orderStatus ?? FlattenedOrderStatuses.Failed),
      },
      {
        id: FlattenedOrderStatuses.Delivering,
        label: formatMessage(messages.orderStatusInfoProgressOutpostDelivering),
        completed: [
          FlattenedOrderStatuses.Delivering,
          FlattenedOrderStatuses.Completed,
        ].includes(orderStatus ?? FlattenedOrderStatuses.Failed),
      },
      {
        id: FlattenedOrderStatuses.Completed,
        label: formatMessage(messages.orderStatusInfoProgressOutpostCompleted),
        completed: [FlattenedOrderStatuses.Completed].includes(
          orderStatus ?? FlattenedOrderStatuses.Failed,
        ),
      },
    ];
  }

  if (orderType === 'delivery') {
    return [
      {
        id: FlattenedOrderStatuses.Received,
        label: formatMessage(messages.orderStatusInfoProgressDeliveryReceived),
        completed: [
          FlattenedOrderStatuses.Received,
          FlattenedOrderStatuses.Preparing,
          FlattenedOrderStatuses.ReadyForCourier,
          FlattenedOrderStatuses.Delivering,
          FlattenedOrderStatuses.Completed,
        ].includes(orderStatus ?? FlattenedOrderStatuses.Failed),
      },
      {
        id: FlattenedOrderStatuses.Preparing,
        label: formatMessage(messages.orderStatusInfoProgressDeliveryPreparing),
        completed: [
          FlattenedOrderStatuses.Preparing,
          FlattenedOrderStatuses.ReadyForCourier,
          FlattenedOrderStatuses.Delivering,
          FlattenedOrderStatuses.Completed,
        ].includes(orderStatus ?? FlattenedOrderStatuses.Failed),
      },
      {
        id: FlattenedOrderStatuses.ReadyForCourier,
        label: formatMessage(messages.orderStatusInfoProgressDeliveryCourier),
        completed: [
          FlattenedOrderStatuses.ReadyForCourier,
          FlattenedOrderStatuses.Delivering,
          FlattenedOrderStatuses.Completed,
        ].includes(orderStatus ?? FlattenedOrderStatuses.Failed),
      },
      {
        id: FlattenedOrderStatuses.Delivering,
        label: formatMessage(
          messages.orderStatusInfoProgressDeliveryDelivering,
        ),
        completed: [
          FlattenedOrderStatuses.Delivering,
          FlattenedOrderStatuses.Completed,
        ].includes(orderStatus ?? FlattenedOrderStatuses.Failed),
      },
    ];
  }

  return [];
};

// ─── Utils ──────────────────────────────────────────────────────────────────────

const checkIfShouldShowProgressBar = (orderStatus?: FlattenedOrderStatuses) =>
  orderStatus !== FlattenedOrderStatuses.Completed &&
  orderStatus !== FlattenedOrderStatuses.Failed;

// ─── Types ──────────────────────────────────────────────────────────────────────

type OrderStatusInfoProps = Readonly<{
  orderId?: string;
  orderTotal?: number;
  orderType?: AddressType;
  orderStatus?: FlattenedOrderStatuses;
  vendorId?: string;
  wantedTime?: string;
  estimatedDeliveryTime?: string;
  locationName?: string;
  locationAddress?: string;
  isTrackable: boolean;
  isPendingFeedback: boolean;
  momoSurveyUrl?: string | null;
  canReorder: boolean;
  isPast: boolean;
  onReorder: () => void;
}>;

type OrderProgressBarProps = Pick<
  OrderStatusInfoProps,
  'orderType' | 'orderStatus'
>;

type OrderStatusInfoHeaderProps = ViewProps &
  OrderStatusTitleProps &
  OrderStatusNumberProps;

type OrderStatusTitleProps = Readonly<{
  title: string;
}>;

type OrderStatusDetailsProps = Readonly<{
  details: string;
}>;

type OrderStatusNumberProps = Pick<OrderStatusInfoProps, 'vendorId'>;

type UseOrderStatusTitleProps = Pick<
  OrderStatusInfoProps,
  'orderStatus' | 'orderType' | 'isTrackable' | 'isPast'
>;

type UseOrderStatusDetailsProps = Pick<
  OrderStatusInfoProps,
  | 'wantedTime'
  | 'estimatedDeliveryTime'
  | 'locationAddress'
  | 'locationName'
  | 'orderId'
  | 'orderType'
  | 'orderStatus'
  | 'orderTotal'
  | 'isTrackable'
  | 'isPendingFeedback'
  | 'isPast'
>;
